import React, { useRef } from 'react';
import { useNode } from '@craftjs/core';
import { useTheme } from '../../../contexts/ThemeContext';
import { Image, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ImageBlock = ({ src, alt, width, alignment }) => {
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);
  const { connectors: { connect, drag }, selected, actions: { setProp } } = useNode((state) => ({
    selected: state.events.selected
  }));

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/uploads`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!response.ok) {
        console.error('Upload failed:', response.status);
        return;
      }
      const data = await response.json();
      if (data.url) {
        setProp(props => props.src = data.url);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  const alignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end'
  };

  return (
    <div
      ref={(ref) => ref && connect(drag(ref))}
      className={`p-4 rounded-lg transition-all ${
        selected ? 'ring-2 ring-[#A2FF00]' : ''
      } ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-gray-100'}`}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleUpload}
        accept="image/*"
        className="hidden"
      />
      {src ? (
        <div className={`flex ${alignmentClasses[alignment] || 'justify-center'}`}>
          <img
            src={src.startsWith('http') ? src : `${BACKEND_URL}${src}`}
            alt={alt}
            style={{ width: width || '100%', maxWidth: '100%' }}
            className="rounded-lg"
          />
        </div>
      ) : (
        <div
          onClick={() => fileInputRef.current?.click()}
          className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer ${
            isDark ? 'border-zinc-600 hover:border-zinc-500' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <Image className={`h-12 w-12 mb-2 ${isDark ? 'text-zinc-500' : 'text-gray-400'}`} />
          <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-gray-500'}`}>Click to upload image</p>
        </div>
      )}
    </div>
  );
};

ImageBlock.craft = {
  displayName: 'Image',
  props: {
    src: '',
    alt: 'Image',
    width: '100%',
    alignment: 'center'
  },
  related: {
    toolbar: ImageBlockSettings
  }
};

function ImageBlockSettings() {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));
  const { isDark } = useTheme();
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${BACKEND_URL}/api/uploads`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      if (!response.ok) {
        console.error('Upload failed:', response.status);
        return;
      }
      const data = await response.json();
      if (data.url) {
        setProp(props => props.src = data.url);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Image
        </label>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleUpload}
          accept="image/*"
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className={`w-full px-4 py-2 rounded-lg border flex items-center justify-center gap-2 ${
            isDark ? 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700' : 'bg-white border-gray-300 hover:bg-gray-50'
          }`}
        >
          <Upload className="h-4 w-4" />
          Upload Image
        </button>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Width
        </label>
        <select
          value={props.width}
          onChange={(e) => setProp(props => props.width = e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border ${isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'}`}
        >
          <option value="50%">Small (50%)</option>
          <option value="75%">Medium (75%)</option>
          <option value="100%">Full Width</option>
        </select>
      </div>
      <div>
        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-zinc-300' : 'text-gray-700'}`}>
          Alignment
        </label>
        <div className="flex gap-2">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              onClick={() => setProp(props => props.alignment = align)}
              className={`flex-1 px-3 py-2 rounded-lg border capitalize ${
                props.alignment === align
                  ? 'bg-[#A2FF00] text-black border-[#A2FF00]'
                  : isDark ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-gray-300'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export { ImageBlockSettings };